import { PrivilegioDTO } from '../models/privilegio.model';

/**
 * Seções em que os módulos são agrupados na tela de grupo de privilégio.
 *
 * A lista de janelas chega do backend (`GET /enums/janelaDeMenu`) na ordem de
 * declaração do enum `JanelaDeMenu`, que não diz nada a quem está montando uma
 * função: "Configurações" aparece entre "Fornecedores" e "Grupo". Aqui a ordem
 * é a mesma em que as telas aparecem no menu, para que a pessoa reconheça o que
 * está liberando. Espelha `SecaoPrivilegio` do mobile (vendas-app).
 *
 * Cada seção lista os nomes do enum do backend — os mesmos valores gravados em
 * `privilegio.janelademenu`.
 */
export interface SecaoPrivilegioDef {
  /** Chave i18n do título da seção, dentro de `{escopo}.secoes`. */
  chaveI18n: string;
  /** Nomes do enum `JanelaDeMenu` que pertencem à seção. */
  janelas: string[];
}

export const SECOES_PRIVILEGIO: SecaoPrivilegioDef[] = [
  { chaveI18n: 'inicio', janelas: ['DASHBOARD'] },
  { chaveI18n: 'pessoas', janelas: ['CLIENTES', 'FORNECEDORES'] },
  {
    chaveI18n: 'artigos',
    janelas: ['ITENS', 'GRUPO', 'SUBGRUPO', 'MARCA', 'UNIDADE_DE_ITEM'],
  },
  {
    chaveI18n: 'operacao',
    janelas: ['VENDAS', 'COMPRA', 'VISUALIZAR_PEDIDOS', 'GESTAO_DE_PEDIDOS'],
  },
  {
    chaveI18n: 'financeiro',
    janelas: [
      'RECEBIMENTOS',
      'GASTOS',
      'TRANSFERENCIAS',
      'CONTAS_A_PAGAR',
      'CONTAS_A_RECEBER',
      'CONTA',
      'EXTRATO_DE_CONTA',
      'RESUMO_DE_CONTA',
    ],
  },
  /** Aba Informes: uma janela por relatório, na ordem da tela. */
  {
    chaveI18n: 'relatorios',
    janelas: [
      'RELATORIO_VENDAS',
      'RELATORIO_ITENS_VENDIDOS',
      'RELATORIO_LISTA_ITENS',
      'RELATORIO_EXTRATO_DE_CAIXA',
      'RELATORIO_CONTAS_PAGAS',
      'RELATORIO_CONTAS_A_PAGAR',
      'RELATORIO_CONTAS_RECEBIDAS',
      'RELATORIO_CONTAS_A_RECEBER',
      'RELATORIO_DRE',
    ],
  },
  /**
   * As duas únicas janelas que não abrem tela nenhuma: são ações checadas no
   * backend (cancelar nota e cancelar movimento). Separadas para não parecerem
   * menus no meio da lista.
   */
  {
    chaveI18n: 'acoes',
    janelas: ['PERMITE_CANCELAR_NOTA', 'PERMITE_CANCELAR_MOVIMENTO'],
  },
  { chaveI18n: 'parceiros', janelas: ['GESTAO_CONTADOR', 'INDICACOES'] },
  {
    chaveI18n: 'ajustes',
    janelas: [
      'CONFIGURACOES',
      'PLANOS',
      'DADOS_DE_COBRANCA',
      'TIMBRADO',
      'CONFIGURACAO_MINISHOP',
      'IMPRESSORAS',
      'COLABORADORES',
      'PRIVILEGIOS',
    ],
  },
];

/**
 * Guarda-chuva das janelas que o backend passar a enviar e que ainda não foram
 * classificadas acima. Sem ela, uma janela nova sumiria da tela e ninguém
 * conseguiria concedê-la.
 */
export const SECAO_PRIVILEGIO_OUTROS = 'outros';

/** Seção já montada para exibição, com os privilégios que caíram nela. */
export interface SecaoDePrivilegio {
  chaveI18n: string;
  titulo: string;
  privilegios: PrivilegioDTO[];
}

/**
 * Distribui os privilégios nas seções, na ordem do menu.
 *
 * Seções vazias são descartadas (a janela pode não existir naquele backend) e o
 * que sobrar sem classificação cai em [SECAO_PRIVILEGIO_OUTROS], no fim.
 *
 * @param privilegios lista vinda de `getJanelasDeMenu()`
 * @param traduzirTitulo resolve a chave da seção para o texto exibido
 */
export function agruparPrivilegiosPorSecao(
  privilegios: PrivilegioDTO[],
  traduzirTitulo: (chaveI18n: string) => string
): SecaoDePrivilegio[] {
  const porJanela = new Map<string, PrivilegioDTO>();
  (privilegios ?? []).forEach(p => {
    const chave = p?.janelaDeMenu?.enum;
    if (chave) {
      porJanela.set(chave, p);
    }
  });

  const classificadas = new Set<string>();
  const secoes: SecaoDePrivilegio[] = [];

  SECOES_PRIVILEGIO.forEach(secao => {
    const itens: PrivilegioDTO[] = [];
    secao.janelas.forEach(janela => {
      const privilegio = porJanela.get(janela);
      if (!privilegio) {
        return;
      }
      itens.push(privilegio);
      classificadas.add(janela);
    });

    if (itens.length) {
      secoes.push({
        chaveI18n: secao.chaveI18n,
        titulo: traduzirTitulo(secao.chaveI18n),
        privilegios: itens,
      });
    }
  });

  const semSecao = Array.from(porJanela.entries())
    .filter(([janela]) => !classificadas.has(janela))
    .map(([, privilegio]) => privilegio);

  if (semSecao.length) {
    secoes.push({
      chaveI18n: SECAO_PRIVILEGIO_OUTROS,
      titulo: traduzirTitulo(SECAO_PRIVILEGIO_OUTROS),
      privilegios: semSecao,
    });
  }

  return secoes;
}
